import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Settings, X } from "lucide-react";
import { api } from "@/api";
import { AutoTradingAdminUnlock } from "@/components/AutoTradingAdminUnlock";

const EXCHANGES = [
  { key: "okx", label: "欧易 OKX", mark: "OKX" },
  { key: "binance", label: "币安 Binance", mark: "BN" },
  { key: "gate", label: "Gate", mark: "GT" },
  { key: "bybit", label: "Bybit", mark: "BY" },
];

const SIGNAL_SOURCES = [
  { key: "gauls", label: "山寨1号机" },
  { key: "unknown", label: "山寨2号机" },
];
const emptyEditor = { user_name: "", uid: "", note: "", follow_sources: ["gauls", "unknown"] };
const exchangeLabel = (key) => EXCHANGES.find((item) => item.key === key)?.label || key;

const remainingDays = (user) => {
  if (user?.unlimited) return null;
  if (!user?.expires_at) return 0;
  const end = new Date(`${String(user.expires_at).slice(0, 10)}T23:59:59`);
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
};

const daysText = (user) => {
  if (user?.unlimited) return "不限";
  const days = remainingDays(user);
  return days <= 0 ? "已到期" : `${days}天`;
};

const tradingStateText = (user) => {
  if (!user?.trading_enabled) return "已关闭";
  if (user?.new_trade_ready) return "已就绪";
  const reason = user?.new_trade_block_reason;
  if (reason === "USER_SUBSCRIPTION_EXPIRED") return "已到期";
  if (reason === "NO_SIGNAL_SOURCE_SELECTED") return "未跟随号机";
  if (reason === "ACCOUNT_TRADING_VALIDATION_REQUIRED") return "待API验证";
  if (reason === "PLATFORM_LIVE_NOT_ENABLED") return "平台Live未开启";
  return "暂不可开仓";
};

function ExchangeMark({ exchange }) {
  const item = EXCHANGES.find((x) => x.key === exchange);
  return <span className={`exchange-mark exchange-${exchange}`}>{item?.mark || "EX"}</span>;
}

function InlineEditModal({ edit, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [unlimited, setUnlimited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!edit) return;
    setMessage("");
    if (edit.type === "leverage") setValue(String(edit.user.max_leverage || 30));
    if (edit.type === "loss") setValue(String(edit.user.max_loss_usdt ?? ""));
    if (edit.type === "days") {
      setUnlimited(Boolean(edit.user.unlimited));
      setValue(String(edit.user.unlimited ? 30 : remainingDays(edit.user)));
    }
  }, [edit]);

  if (!edit) return null;
  const title = edit.type === "leverage" ? "修改杠杆" : edit.type === "loss" ? "修改单笔最大亏损" : "修改使用天数";
  const label = edit.type === "leverage" ? "最大杠杆上限（倍）" : edit.type === "loss" ? "单笔最大亏损（USDT）" : "剩余使用天数";

  const save = async () => {
    setBusy(true); setMessage("");
    try {
      let payload;
      if (edit.type === "leverage") payload = { max_leverage: Math.min(30, Math.max(1, Number(value || 1))) };
      else if (edit.type === "loss") payload = { max_loss_usdt: Math.max(0.01, Number(value || 0.01)) };
      else if (unlimited) payload = { unlimited: true, expires_at: null };
      else {
        const days = Math.max(0, Number(value || 0));
        const expires = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
        payload = { unlimited: false, expires_at: expires };
      }
      await api.patch(`/managed-users/${edit.user.user_id}`, payload);
      await onSaved();
      onClose();
    } catch (err) {
      setMessage(err.response?.data?.detail || "保存失败");
    } finally { setBusy(false); }
  };

  return <div className="user-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className="user-modal small-user-modal">
      <div className="user-modal-head"><div><p className="eyebrow">{edit.user.user_name}</p><h2>{title}</h2></div>
        <button className="icon-button" onClick={onClose}><X size={17} /></button></div>
      <div className="user-modal-body">
        <label className="single-edit-field">{label}<input type="number"
          min={edit.type === "leverage" ? "1" : edit.type === "loss" ? "0.01" : "0"}
          max={edit.type === "leverage" ? "30" : undefined}
          step={edit.type === "leverage" ? "1" : edit.type === "loss" ? "0.01" : "1"}
          disabled={edit.type === "days" && unlimited}
          value={value} onChange={(e) => setValue(e.target.value)} /></label>
        {edit.type === "leverage" && <p className="field-hint">合约统一使用全仓；实际杠杆取“用户上限”和“当前执行交易所该币种最大可用杠杆”中的较小值，单笔最大亏损仍按止损距离独立计算。</p>}
        {edit.type === "days" && <label className="inline-user-check standalone"><input type="checkbox" checked={unlimited}
          onChange={(e) => setUnlimited(e.target.checked)} /> 不限制使用天数</label>}
        {message && <p className="action-message">{message}</p>}
        <div className="user-modal-actions"><button className="secondary" onClick={onClose}>取消</button><button onClick={save} disabled={busy}>保存</button></div>
      </div>
    </div>
  </div>;
}

function UserEditor({ open, user, onClose, onSaved }) {
  const [form, setForm] = useState(emptyEditor);
  const [exchange, setExchange] = useState("okx");
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(user ? { user_name: user.user_name || "", uid: user.uid || "", note: user.note || "",
      follow_sources: user.follow_sources || ["gauls", "unknown"] } : emptyEditor);
    setExchange(user?.exchanges?.[0]?.exchange || "okx");
    setApiKey(""); setSecretKey(""); setPassphrase(""); setMessage("");
  }, [open, user]);

  if (!open) return null;
  const selectedMeta = user?.exchanges?.find((item) => item.exchange === exchange);
  const ownerLegacyOkx = user?.user_id === "__owner__" && exchange === "okx" && selectedMeta?.configured;

  const saveProfile = async () => {
    setBusy(true); setMessage("");
    try {
      if (user) {
        await api.patch(`/managed-users/${user.user_id}`, form);
        setMessage("用户设置已保存");
        await onSaved();
      } else {
        const result = (await api.post("/managed-users", form)).data;
        setMessage("用户已新增");
        await onSaved();
        if (result?.user) onClose();
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || "保存失败");
    } finally { setBusy(false); }
  };

  const saveCredential = async () => {
    if (!user) { setMessage("请先保存用户，再配置交易所 API"); return; }
    if (!apiKey || !secretKey || (exchange === "okx" && !passphrase)) {
      setMessage(exchange === "okx" ? "请填写 API Key、Secret Key 和 Passphrase" : "请填写 API Key 和 Secret Key");
      return;
    }
    setBusy(true); setMessage("");
    try {
      const result = (await api.post(`/managed-users/${user.user_id}/exchanges/${exchange}/credentials`, {
        api_key: apiKey, secret_key: secretKey, passphrase: exchange === "okx" ? passphrase : undefined,
      })).data;
      if (!result.ok) throw new Error(result.detail || "API 验证失败");
      setApiKey(""); setSecretKey(""); setPassphrase("");
      setMessage(`${exchangeLabel(exchange)} API 已验证，余额监听已启动`);
      await onSaved();
    } catch (err) {
      setMessage(err.response?.data?.detail || err.message || "API 保存失败");
    } finally { setBusy(false); }
  };

  const deleteCredential = async () => {
    if (!user || !selectedMeta?.configured || ownerLegacyOkx) return;
    setBusy(true); setMessage("");
    try {
      const result = (await api.delete(`/managed-users/${user.user_id}/exchanges/${exchange}/credentials`)).data;
      if (!result.ok) throw new Error(result.detail || "移除 API 失败");
      setMessage(`${exchangeLabel(exchange)} API 已移除`);
      await onSaved();
    } catch (err) {
      setMessage(err.response?.data?.detail || "移除 API 失败");
    } finally { setBusy(false); }
  };

  return <div className="user-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className="user-modal">
      <div className="user-modal-head"><div><p className="eyebrow">用户配置</p><h2>{user ? "用户设置" : "新增用户"}</h2></div>
        <button className="icon-button" onClick={onClose}><X size={17} /></button></div>
      <div className="user-modal-body">
        <div className="user-form-grid compact-user-form">
          <label>交易所用户名<input value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} placeholder="填写用户在交易所使用的名称" /></label>
          <label>交易所 UID（可选）<input value={form.uid || ""} onChange={(e) => setForm({ ...form, uid: e.target.value })} placeholder="Gate 建议填写 UID 以启用实时推送" /></label>
          <label className="full-user-field">备注<input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="可选" /></label>
          <div className="full-user-field"><span className="managed-field-label">跟随号机</span>
            <div className="follow-source-row">{SIGNAL_SOURCES.map((source) => {
              const checked = (form.follow_sources || []).includes(source.key);
              return <label key={source.key} className={`follow-source-pill ${checked ? "active" : ""}`}>
                <input type="checkbox" checked={checked} onChange={(e) => {
                  const current = new Set(form.follow_sources || []);
                  if (e.target.checked) current.add(source.key); else current.delete(source.key);
                  setForm({ ...form, follow_sources: [...current] });
                }} /> {source.label}
              </label>;
            })}</div>
            <small className="managed-field-help">只影响新开仓；已存在仓位仍会继续接收减仓、止损和保本更新。</small>
          </div>
        </div>
        <div className="user-modal-actions"><button onClick={saveProfile} disabled={busy || !form.user_name.trim()}>{user ? "保存用户信息" : "新增用户"}</button></div>
        {!user && <p className="managed-field-help">新用户默认关闭自动交易。完成 API 验证、风险/杠杆、使用期限和跟随号机设置后，再从用户列表开启。</p>}

        {user && <div className="exchange-credential-section">
          <div className="section-heading compact"><div><p className="eyebrow">交易所 API</p><h2>选择交易所并配置</h2></div></div>
          <div className="exchange-tabs">{EXCHANGES.map((item) => <button key={item.key}
            className={exchange === item.key ? "active" : ""} onClick={() => { setExchange(item.key); setMessage(""); }}>
            <ExchangeMark exchange={item.key} />{item.label}</button>)}</div>
          {ownerLegacyOkx ? <div className="credential-existing-note">
            <b>当前 OKX 使用现有 Live API 配置</b><span>这里不重复保存密钥，避免出现两套配置。</span>
            <Link to="/auto-trading">进入 OKX Live API 设置</Link>
          </div> : <div className="credential-form managed-credential-form">
            <label>API Key<input type="password" autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={selectedMeta?.configured ? "已配置；更换时重新输入" : "请输入 API Key"} /></label>
            <label>Secret Key<input type="password" autoComplete="off" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder={selectedMeta?.configured ? "已配置；更换时重新输入" : "请输入 Secret Key"} /></label>
            {exchange === "okx" && <label>Passphrase<input type="password" autoComplete="off" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} placeholder={selectedMeta?.passphrase_configured ? "已配置；更换时重新输入" : "请输入 Passphrase"} /></label>}
            <div className="credential-form-actions"><button onClick={saveCredential} disabled={busy}>验证并保存 API</button>
              {selectedMeta?.configured && <button className="secondary danger-text" onClick={deleteCredential} disabled={busy}>移除该交易所</button>}</div>
          </div>}
          <p className="credential-privacy">密钥只在后端加密保存，前端不会读取 Secret Key / Passphrase；不要开启提现权限。自动跟单执行目前只支持 OKX，其他交易所 API 仅用于余额展示。</p>
        </div>}
        {message && <p className="action-message">{message}</p>}
      </div>
    </div>
  </div>;
}

export default function UserManagementPage() {
  const [auth, setAuth] = useState(null);
  const [authConfigured, setAuthConfigured] = useState(true);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exchangeFilter, setExchangeFilter] = useState("all");
  const [editor, setEditor] = useState({ open: false, user: null });
  const [inlineEdit, setInlineEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try { setUsers((await api.get("/managed-users")).data.items || []); setError(""); }
    catch (err) { if (err.response?.status === 401) setAuth(false); else setError("用户数据加载失败"); }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      let result = (await api.get("/auto-trading/auth/session")).data;
      setAuthConfigured(result.auth_configured);
      if (!result.authenticated) {
        try { result = (await api.post("/auto-trading/auth/refresh")).data; } catch { /* unlock below */ }
      }
      setAuth(Boolean(result.authenticated));
      if (result.authenticated) load();
    } catch { setAuth(false); }
  }, [load]);

  useEffect(() => { checkSession(); }, [checkSession]);
  useEffect(() => {
    if (!auth) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [auth, load]);

  const login = async (password) => {
    setBusy(true); setError("");
    try { await api.post("/auto-trading/auth/login", { password }); setAuth(true); await load(); }
    catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail === "INVALID_ADMIN_PASSWORD" ? "管理员密码错误" : "管理员登录失败");
      if (detail === "ADMIN_AUTH_NOT_CONFIGURED") setAuthConfigured(false);
    } finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((user) => {
      const exs = (user.exchanges || []).map((item) => item.exchange);
      const searchOk = !q || [user.user_name, user.uid, ...exs.map(exchangeLabel)].filter(Boolean).join(" ").toLowerCase().includes(q);
      const statusOk = statusFilter === "all" || (statusFilter === "on" ? user.trading_enabled : !user.trading_enabled);
      const exchangeOk = exchangeFilter === "all" || exs.includes(exchangeFilter);
      return searchOk && statusOk && exchangeOk;
    });
  }, [users, query, statusFilter, exchangeFilter]);

  const toggleTrading = async (user) => {
    setUpdatingId(user.user_id);
    try {
      await api.patch(`/managed-users/${user.user_id}`, { trading_enabled: !user.trading_enabled });
      await load();
    } catch (err) { setError(err.response?.data?.detail || "交易状态修改失败"); }
    finally { setUpdatingId(null); }
  };

  return <section data-testid="managed-users-page">
    <div className="page-heading"><div><p className="eyebrow">多账户管理</p><h1>用户管理</h1></div>
      {auth && <button onClick={() => setEditor({ open: true, user: null })}><Plus size={15} /> 新增用户</button>}</div>
    {auth === null && <p className="empty-state bordered">正在检查管理员会话…</p>}
    {auth === false && <AutoTradingAdminUnlock busy={busy} error={error} configured={authConfigured} onLogin={login} />}
    {auth && <>
      {error && <p className="error-text">{error}</p>}
      <div className="user-search-panel">
        <div className="user-search-input"><Search size={15} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索交易所用户名 / UID / 交易所" /></div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="all">全部状态</option><option value="on">已开启</option><option value="off">已关闭</option></select>
        <select value={exchangeFilter} onChange={(e) => setExchangeFilter(e.target.value)}><option value="all">全部交易所</option>{EXCHANGES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}</select>
      </div>
      <div className="table-frame managed-user-table"><table><thead><tr>
        <th>交易所用户名</th><th>交易所</th><th>交易状态</th><th>API 信息</th><th>跟随号机</th><th>杠杆</th><th>单笔最大亏损</th><th>剩余使用天数</th><th>操作</th>
      </tr></thead><tbody>{filtered.map((user) => <tr key={user.user_id}>
        <td><div className="managed-user-name"><b>{user.user_name}</b>{user.is_owner && <span>本人账户</span>}<Link to={`/users/${user.user_id}`}>查看交易</Link></div></td>
        <td><div className="managed-exchange-list">{(user.exchanges || []).length ? user.exchanges.map((item) => <span key={item.exchange} className="managed-exchange-chip"><ExchangeMark exchange={item.exchange} />{exchangeLabel(item.exchange)}</span>) : <span className="muted">未配置</span>}</div></td>
        <td><button type="button" className={`managed-switch ${user.trading_enabled ? "on" : ""}`} disabled={updatingId === user.user_id} onClick={() => toggleTrading(user)} aria-label="切换交易状态"><span /></button><small className={user.new_trade_ready ? "state-on" : "state-off"}>{tradingStateText(user)}</small></td>
        <td><div className="api-status-list">{(user.exchanges || []).length ? user.exchanges.map((item) => <span key={item.exchange}><ExchangeMark exchange={item.exchange} />{item.configured ? `${item.trading_ready || item.exchange !== "okx" ? "已配置" : "待交易验证"} ${item.api_key_masked || ""}` : "未配置"}</span>) : "—"}</div></td>
        <td><div className="managed-follow-list">{SIGNAL_SOURCES.filter((source) => (user.follow_sources || []).includes(source.key)).map((source) => <span key={source.key}>{source.label}</span>)}{!(user.follow_sources || []).length && <span className="muted">不跟随</span>}</div></td>
        <td><button className="inline-value-button" onClick={() => setInlineEdit({ type: "leverage", user })}><b className="mono">{user.max_leverage || 30}x</b><small>点击修改</small></button></td>
        <td><button className="inline-value-button" onClick={() => setInlineEdit({ type: "loss", user })}><b className="mono">{user.max_loss_usdt ?? "未设置"} USDT</b><small>点击修改</small></button></td>
        <td><button className="inline-value-button" onClick={() => setInlineEdit({ type: "days", user })}><b>{daysText(user)}</b><small>点击修改</small></button></td>
        <td><button className="secondary" onClick={() => setEditor({ open: true, user })}><Settings size={14} /> 设置</button></td>
      </tr>)}</tbody></table></div>
      {!filtered.length && <p className="empty-state">暂无用户</p>}
    </>}
    <UserEditor open={editor.open} user={editor.user} onClose={() => setEditor({ open: false, user: null })} onSaved={load} />
    <InlineEditModal edit={inlineEdit} onClose={() => setInlineEdit(null)} onSaved={load} />
  </section>;
}
